/**
 * What a fight camera does besides keeping both fighters in shot (step 98).
 *
 * `framing.js` answers *where must the camera be to see them both*, every
 * frame, and nothing else — which is right for a fight in progress and
 * lifeless at the moment that matters most. This is the rest, as a broadcast
 * director would do it:
 *
 *   - **drift**: a slow wander of a few centimetres, three sine waves at
 *     unrelated periods, so a held shot breathes instead of sitting dead still.
 *     Half the amplitude moved the picture about five pixels at 720p over ten
 *     seconds and could not be seen; this is ten to fifteen, at under two
 *     millimetres a frame — visible as a camera, never as a fighter moving.
 *   - **the push**: on a knockout, the camera eases in on the finish — a
 *     shorter distance, the aim drawn towards the loser.
 *   - **the hold**: and stays there. After a moment to let the blow carry the
 *     body, the shot stops following and holds where the finish happened,
 *     until the next round's intro. A body that went over the parapet does not
 *     drag the camera after it into the street.
 *
 * The hitstop and the shake are `impact.js`'s and are left exactly as they
 * were; the shake is added on top of whatever this says.
 *
 * Pure: plain points in, plain numbers out, every number in `feel.json`.
 */

import FEEL from './feel.json';

export const DIRECTOR = FEEL.camera;

const ease = (t) => (t <= 0 ? 0 : t >= 1 ? 1 : t * t * (3 - 2 * t));

export function createDirector(config = DIRECTOR) {
  let time = 0;
  /** Seconds since the knockout, or null before one. */
  let since = null;
  let loser = null;
  let held = null;

  return {
    config,
    /** True from the knockout to the next intro. */
    get finishing() {
      return since !== null;
    },
    get held() {
      return held !== null;
    },

    /** A fighter is finished. `at` is where the loser is, on the floor. */
    knockout(at) {
      if (since !== null) return;
      since = 0;
      loser = at ? { x: at.x, z: at.z } : null;
    },

    /** Back to following the fight: the next intro. */
    reset() {
      since = null;
      loser = null;
      held = null;
    },

    update(dt) {
      if (!(dt > 0)) return;
      time += dt;
      if (since !== null) since += dt;
    },

    /**
     * The shot for this frame, given where the fighters are.
     *
     *   points  what `solveFraming` should keep in shot — the live points,
     *           or the ones held since the finish
     *   push    the fraction of the solved distance to stand at (1 = none)
     *   aim     `{x, z, weight}`: draw the aim this far towards the loser
     *   drift   `{x, y, z}` metres to add to the camera's position, and
     *   look    `{x, y, z}` metres to add to where it looks
     */
    shot(points) {
      const { push, hold, drift } = config;
      let into = 0;
      let framed = points;
      if (since !== null) {
        into = ease(since / push.time);
        // Followed for a beat, so the blow is seen to carry the body; then held.
        if (since >= hold.follow && !held && points?.length) held = points.map((p) => ({ x: p.x, z: p.z }));
        if (held) framed = held;
      }
      // The drift breathes less as the camera pushes in: a held finish is held.
      const wander = 1 - into * (1 - drift.whileHeld);
      const wave = (period, phase) => Math.sin((time / period) * Math.PI * 2 + phase);
      return {
        points: framed,
        push: 1 - (1 - push.amount) * into,
        aim: loser ? { x: loser.x, z: loser.z, weight: push.aim * into } : null,
        drift: {
          x: wave(drift.periods[0], 0) * drift.position * wander,
          y: wave(drift.periods[1], 1.3) * drift.position * 0.6 * wander,
          z: wave(drift.periods[2], 2.1) * drift.position * wander,
        },
        look: {
          x: wave(drift.periods[2], 0.7) * drift.aim * wander,
          y: wave(drift.periods[0], 2.6) * drift.aim * 0.5 * wander,
          z: wave(drift.periods[1], 4.2) * drift.aim * wander,
        },
      };
    },
  };
}
